import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { useIsMobile } from '../hooks/useMediaQuery';
import { fetchAttendedFestivalIds } from '../attendedUtils';

// Festival Browse (SHARC_Spec_Festival_Browse_2026-10-07) — brought up to
// ArtistList.js's level: two filter rows, sortable columns, mobile sort row,
// plus FestivalDetail.js's jump-to-page pagination.

// Normalize for search: strip diacritics and punctuation so "motorhead"
// matches "Motörhead" and "carissas" matches "Carissa's Wierd".
const normalizeStr = (str) =>
    (str || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/['‘’‛`\-.,:!?&]/g, '')
        .toLowerCase();

const PAGE_SIZE = 25;

const NUMERIC_COLS = new Set(['max_capacity', 'total_shows']);
const DATE_COLS    = new Set(['end_date']);

// Shared between the desktop table headers and the mobile "Sort by" select,
// so both stay in sync automatically if columns ever change.
// Dates sorts on end_date (the festival's most recent show) — the same value
// the Most Recent Show filter reads, and the default newest-first order.
const SORT_COLUMNS = [
    { col: 'name',         label: 'Festival' },
    { col: 'city',         label: 'City' },
    { col: 'state',        label: 'State' },
    { col: 'country',      label: 'Country' },
    { col: 'max_capacity', label: 'Max Stage Capacity' },
    { col: 'total_shows',  label: '# of Shows' },
    { col: 'end_date',     label: 'Dates' },
];

const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

// Unchanged from the pre-redesign list: a single date when the festival's
// first and last shows fall on the same day, otherwise "start – end".
const formatDateRange = (festival) => {
    const start = formatDate(festival.start_date);
    const end = formatDate(festival.end_date);
    return start && end && start !== end ? `${start} – ${end}` : start || '—';
};

const formatCapacity = (cap) => (cap == null ? '—' : Number(cap).toLocaleString());

// Distinct { city, state, country } across the festival's shows (series-
// pooled server-side). City search and the Country/State filters match ANY
// of these, so a series or multi-venue festival showing "Multiple" in a
// column is still found by each of its locations.
const locationsOf = (festival) => (Array.isArray(festival.locations) ? festival.locations : []);

// ── Jump-to-page pagination (local copy of FestivalDetail.js's Pagination —
// copy-per-file convention, see context doc Section 12) ────────────────────
function JumpPagination({ currentPage, totalItems, onPageChange, pageSize = PAGE_SIZE }) {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    if (totalPages <= 1) return null;
    return (
        <div style={S.pageJumpWrap}>
            <span style={S.pageJumpLabel}>Page:</span>
            <select
                value={currentPage}
                onChange={e => onPageChange(Number(e.target.value))}
                style={S.pageJumpSelect}
            >
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <option key={p} value={p}>{p}</option>
                ))}
            </select>
            <span style={S.pageJumpLabel}>of {totalPages}</span>
        </div>
    );
}

// ── Mobile card (replaces the table below 768px — see
// SHARC_Mobile_Responsive_Spec_v1.1, §5.3) ──────────────────────────────────
function FestivalCardMobile({ festival }) {
    const location = [festival.city, festival.state, festival.country].filter(Boolean).join(', ');
    const shows = Number(festival.total_shows) || 0;
    return (
        <Link to={`/festivals/${festival.slug || festival.id}`} style={mobileStyles.card}>
            <div style={mobileStyles.cardName}>
                {festival.name}{festival.is_parent ? ' (Series)' : ''}
            </div>
            {location && <div style={mobileStyles.cardLocation}>{location}</div>}
            <div style={mobileStyles.cardStatsRow}>
                <span>{shows} {shows === 1 ? 'show' : 'shows'}</span>
                {festival.max_capacity != null && (
                    <span>Max Cap {formatCapacity(festival.max_capacity)}</span>
                )}
                <span style={mobileStyles.cardDates}>{formatDateRange(festival)}</span>
            </div>
        </Link>
    );
}

function FestivalList() {
    const isMobile = useIsMobile();
    const [festivals, setFestivals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);

    // Picklists for code→name lookup in filter dropdowns
    const [picklists, setPicklists] = useState(null);

    // Filter state
    const [search, setSearch]                 = useState('');
    const [filterAttended, setFilterAttended] = useState('');
    const [filterKind, setFilterKind]         = useState('');
    const [filterCountry, setFilterCountry]   = useState('');
    const [filterState, setFilterState]       = useState('');
    const [dateFrom, setDateFrom]             = useState('');
    const [dateTo, setDateTo]                 = useState('');

    // Attended/Not Attended data — best-effort authenticated bulk fetch, same
    // contract as ArtistList.js's Seen filter: left empty/false on any failure
    // (including simply not being logged in).
    const [attendedFestivalIds, setAttendedFestivalIds] = useState(new Set());
    const [isLoggedIn, setIsLoggedIn]                   = useState(false);

    // Sort state — default Dates, newest first (spec §3.4)
    const [sortCol, setSortCol] = useState('end_date');
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage]       = useState(1);

    useEffect(() => {
        document.title = 'SHARC - Festivals';
        Promise.all([
            axios.get(`${config.API_URL}/festivals`),
            axios.get(`${config.API_URL}/picklists`)
        ])
            .then(([festivalsRes, picklistsRes]) => {
                setFestivals(festivalsRes.data.festivals || []);
                setPicklists(picklistsRes.data);
                setLoading(false);
            })
            .catch(() => { setError('Failed to load festivals'); setLoading(false); });
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, []);

    useEffect(() => {
        fetchAttendedFestivalIds()
            .then(ids => { setAttendedFestivalIds(new Set(ids)); setIsLoggedIn(true); })
            .catch(() => { /* not logged in, or fetch failed — leave defaults */ });
    }, []);

    // code→name lookup maps built from picklists
    const countryNameMap = useMemo(() => {
        if (!picklists) return {};
        return Object.fromEntries(picklists.countries.map(c => [c.code, c.name]));
    }, [picklists]);

    // Key: "countryId|stateCode" — avoids collisions where two countries share a code (e.g. CO)
    const stateNameMap = useMemo(() => {
        if (!picklists) return {};
        return Object.fromEntries(picklists.states_regions.map(s => [`${s.country_id}|${s.code}`, s.name]));
    }, [picklists]);

    // Resolve the picklist country_id for the currently selected country code
    const selectedCountryPicklistId = useMemo(() => {
        if (!picklists || !filterCountry) return null;
        const match = picklists.countries.find(c => c.code === filterCountry);
        return match ? match.id : null;
    }, [picklists, filterCountry]);

    // Filter options come from every location any festival's shows were in,
    // not the (possibly "Multiple") display columns.
    const countries = useMemo(() => {
        const unique = [...new Set(festivals.flatMap(f => locationsOf(f).map(l => l.country)).filter(Boolean))];
        return unique.sort((a, b) => (countryNameMap[a] || a).localeCompare(countryNameMap[b] || b));
    }, [festivals, countryNameMap]);
    const states = useMemo(() => {
        const locs = festivals.flatMap(f => locationsOf(f));
        const src = filterCountry ? locs.filter(l => l.country === filterCountry) : locs;
        return [...new Set(src.map(l => l.state).filter(Boolean))].sort();
    }, [festivals, filterCountry]);

    const handleSort = (col) => {
        if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortCol(col); setSortDir('asc'); }
        setPage(1);
    };

    const filtered = useMemo(() => {
        const q = normalizeStr(search);
        return festivals.filter(f => {
            const locs = locationsOf(f);
            if (search) {
                const nameHit = f.name && normalizeStr(f.name).includes(q);
                const cityHit = locs.some(l => l.city && normalizeStr(l.city).includes(q));
                if (!nameHit && !cityHit) return false;
            }
            // Attended / Not Attended both exclude series (spec §3.2).
            // Logged-out + either option → zero results (explicit check rather
            // than relying on an empty set, which would make "Not Attended"
            // incorrectly match every festival).
            if (filterAttended) {
                if (!isLoggedIn) return false;
                if (f.is_parent) return false;
                if (filterAttended === 'attended'     && !attendedFestivalIds.has(f.id)) return false;
                if (filterAttended === 'not_attended' &&  attendedFestivalIds.has(f.id)) return false;
            }
            if (filterKind === 'festival' &&  f.is_parent) return false;
            if (filterKind === 'series'   && !f.is_parent) return false;
            // Country/State match any one location (state checked within the
            // selected country when both are set).
            if (filterCountry || filterState) {
                const hit = locs.some(l =>
                    (!filterCountry || l.country === filterCountry) &&
                    (!filterState   || l.state === filterState)
                );
                if (!hit) return false;
            }
            if (dateFrom && (!f.end_date || f.end_date.slice(0, 10) < dateFrom)) return false;
            if (dateTo   && (!f.end_date || f.end_date.slice(0, 10) > dateTo))   return false;
            return true;
        });
    }, [festivals, search, filterAttended, isLoggedIn, attendedFestivalIds, filterKind, filterCountry, filterState, dateFrom, dateTo]);

    const sorted = useMemo(() => {
        return [...filtered].sort((a, b) => {
            const av = NUMERIC_COLS.has(sortCol) ? Number(a[sortCol] ?? 0) : (a[sortCol] ?? '');
            const bv = NUMERIC_COLS.has(sortCol) ? Number(b[sortCol] ?? 0) : (b[sortCol] ?? '');
            const cmp = NUMERIC_COLS.has(sortCol)
                ? av - bv
                : DATE_COLS.has(sortCol)
                    ? (av || '').localeCompare(bv || '')
                    : String(av).localeCompare(String(bv));
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [filtered, sortCol, sortDir]);

    const clearFilters = () => {
        setSearch(''); setFilterAttended(''); setFilterKind('');
        setFilterCountry(''); setFilterState('');
        setDateFrom(''); setDateTo('');
        setPage(1);
    };

    const hasFilters = search || filterAttended || filterKind || filterCountry || filterState || dateFrom || dateTo;

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return <span style={S.sortIcon}>↕</span>;
        return <span style={{ ...S.sortIcon, color: '#bb86fc' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
    };

    if (loading) return <div style={S.message}>Loading festivals...</div>;
    if (error)   return <div style={S.errMsg}>{error}</div>;

    const pageItems = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            {/* ── Header ── */}
            <div style={isMobile ? { ...S.header, ...Smobile.header } : S.header}>
                <h1 style={S.title}>Festivals</h1>
                <div style={S.headerRight}>
                    <span style={S.count}>{sorted.length.toLocaleString()} of {festivals.length.toLocaleString()}</span>
                    <Link to="/add-festival" style={S.addButton}>+ Add Festival</Link>
                </div>
            </div>

            {/* ── Filters Row 1: text search + date range ── */}
            <div style={S.filterBar}>
                <input
                    type="text"
                    placeholder="Search by festival name or city..."
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                    style={S.searchInput}
                />
                <span style={S.dateLabel}>Most Recent Show</span>
                <input
                    type="date"
                    value={dateFrom}
                    onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                    style={S.dateInput}
                />
                <span style={S.capDash}>–</span>
                <input
                    type="date"
                    value={dateTo}
                    onChange={e => { setDateTo(e.target.value); setPage(1); }}
                    style={S.dateInput}
                />
                {hasFilters && (
                    <button onClick={clearFilters} style={S.clearBtn}>✕ Clear all</button>
                )}
            </div>

            {/* ── Filters Row 2: dropdowns ── */}
            <div style={S.filterBar}>
                <select value={filterAttended} onChange={e => { setFilterAttended(e.target.value); setPage(1); }} style={{ ...S.filterSelect, flex: '1 1 140px' }}>
                    <option value="">Attended or Not Attended</option>
                    <option value="attended">Attended</option>
                    <option value="not_attended">Not Attended</option>
                </select>
                <select value={filterKind} onChange={e => { setFilterKind(e.target.value); setPage(1); }} style={{ ...S.filterSelect, flex: '1 1 140px' }}>
                    <option value="">Festival or Festival Series</option>
                    <option value="festival">Festival</option>
                    <option value="series">Festival Series</option>
                </select>
                <select value={filterCountry} onChange={e => { setFilterCountry(e.target.value); setFilterState(''); setPage(1); }} style={{ ...S.filterSelect, flex: '1 1 140px' }}>
                    <option value="">All Countries</option>
                    {countries.map(c => (
                        <option key={c} value={c}>{countryNameMap[c] || c}</option>
                    ))}
                </select>
                <select value={filterState} onChange={e => { setFilterState(e.target.value); setPage(1); }} style={{ ...S.filterSelect, flex: '1 1 140px' }} disabled={states.length === 0}>
                    <option value="">All States</option>
                    {states.map(s => (
                        <option key={s} value={s}>{stateNameMap[`${selectedCountryPicklistId}|${s}`] || s}</option>
                    ))}
                </select>
            </div>

            {/* ── Mobile-only sort control (table headers below handle this on desktop) ── */}
            {isMobile && (
                <div style={mobileStyles.sortRow}>
                    <select
                        value={sortCol}
                        onChange={e => { setSortCol(e.target.value); setPage(1); }}
                        style={mobileStyles.sortSelect}
                    >
                        {SORT_COLUMNS.map(({ col, label }) => (
                            <option key={col} value={col}>Sort: {label}</option>
                        ))}
                    </select>
                    <button
                        onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                        style={mobileStyles.sortDirBtn}
                        aria-label={sortDir === 'asc' ? 'Ascending' : 'Descending'}
                    >
                        {sortDir === 'asc' ? '↑ Asc' : '↓ Desc'}
                    </button>
                </div>
            )}

            {sorted.length === 0 ? (
                <div style={S.message}>
                    {hasFilters ? 'No festivals match your filters.' : 'No festivals recorded yet.'}
                </div>
            ) : isMobile ? (
                <div style={mobileStyles.cardList}>
                    {pageItems.map((f) => (
                        <FestivalCardMobile key={f.id} festival={f} />
                    ))}
                </div>
            ) : (
                <div style={S.tableContainer}>
                    <table style={S.table}>
                        <thead>
                            <tr>
                                {SORT_COLUMNS.map(({ col, label }) => (
                                    <th
                                        key={col}
                                        style={{ ...S.th, ...(NUMERIC_COLS.has(col) ? S.thNum : {}), cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort(col)}
                                    >
                                        {label} <SortIcon col={col} />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pageItems.map((f, idx) => (
                                <tr
                                    key={f.id}
                                    style={idx % 2 === 0 ? S.trEven : S.trOdd}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#252525'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#1e1e1e' : '#1a1a1a'}
                                >
                                    <td style={S.td}>
                                        <Link to={`/festivals/${f.slug || f.id}`} style={S.link}>
                                            {f.name}{f.is_parent ? ' (Series)' : ''}
                                        </Link>
                                    </td>
                                    <td style={S.td}>{f.city || '—'}</td>
                                    <td style={S.td}>{f.state || '—'}</td>
                                    <td style={S.td}>{f.country || '—'}</td>
                                    <td style={{ ...S.td, ...S.numCell }}>{formatCapacity(f.max_capacity)}</td>
                                    <td style={{ ...S.td, ...S.numCell }}>{Number(f.total_shows) || 0}</td>
                                    <td style={S.td}>{formatDateRange(f)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <JumpPagination currentPage={page} totalItems={sorted.length} onPageChange={setPage} />
        </div>
    );
}

const S = {
    header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    title:       { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', margin: 0 },
    headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
    count:       { fontSize: '13px', color: '#666' },
    addButton:   { backgroundColor: '#bb86fc', color: '#121212', textDecoration: 'none', padding: '8px 18px', borderRadius: '4px', fontWeight: 'bold', fontSize: '14px' },

    filterBar:    { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'center' },
    searchInput:  { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '9px 14px', fontSize: '14px', color: '#e0e0e0', flex: '1 1 260px', minWidth: '200px', boxSizing: 'border-box' },
    filterSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '9px 12px', fontSize: '13px', color: '#e0e0e0', flex: '0 1 140px', minWidth: '110px', boxSizing: 'border-box' },
    clearBtn:     { backgroundColor: 'transparent', color: '#888', border: '1px solid #444', borderRadius: '6px', padding: '9px 12px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },

    tableContainer: { overflowX: 'auto' },
    table:          { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th:             { textAlign: 'left', padding: '10px 14px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    thNum:          { textAlign: 'right' },
    td:             { padding: '10px 14px', color: '#e0e0e0', borderBottom: '1px solid #222', whiteSpace: 'nowrap' },
    trEven:         { backgroundColor: '#1e1e1e' },
    trOdd:          { backgroundColor: '#1a1a1a' },
    link:           { color: '#bb86fc', textDecoration: 'none' },
    numCell:        { textAlign: 'right', color: '#aaa', fontVariantNumeric: 'tabular-nums' },
    dateLabel:  { fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
    dateInput:  { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', color: '#e0e0e0', width: '130px', boxSizing: 'border-box' },
    capDash:    { color: '#555', fontSize: '14px' },
    sortIcon:   { fontSize: '10px', marginLeft: '4px', color: '#555' },
    message:        { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    errMsg:         { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },

    // Jump-to-page pagination — copied from FestivalDetail.js
    pageJumpWrap:   { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' },
    pageJumpLabel:  { fontSize: '13px', color: '#aaa', fontWeight: '600' },
    pageJumpSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '6px 10px', fontSize: '14px', color: '#e0e0e0', cursor: 'pointer', fontWeight: 'bold', width: '64px', flexShrink: 0, textAlign: 'center', textAlignLast: 'center' },
};

// ── Mobile style overrides (Spec: SHARC_Mobile_Responsive_Spec_v1.1, §4.3/§5.3) ──
const Smobile = {
    // "Festivals" heading + count/+Add group can overflow a nowrap flex row on
    // narrow viewports — allow it to wrap onto a second line instead.
    header: {
        flexWrap: 'wrap',
        gap: '10px',
    },
};

// Kept as a separate object, only used when useIsMobile() is true, so the
// desktop table above is completely untouched.
const mobileStyles = {
    sortRow: {
        display: 'flex',
        gap: '8px',
        marginBottom: '16px',
    },
    sortSelect: {
        flex: '1 1 auto',
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '6px',
        padding: '9px 12px',
        fontSize: '13px',
        color: '#e0e0e0',
        boxSizing: 'border-box',
    },
    sortDirBtn: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '6px',
        padding: '9px 14px',
        fontSize: '13px',
        color: '#e0e0e0',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    cardList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    card: {
        display: 'block',
        backgroundColor: '#1e1e1e',
        border: '1px solid #2e2e2e',
        borderRadius: '8px',
        padding: '14px 16px',
        textDecoration: 'none',
        color: '#e0e0e0',
    },
    cardName: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#bb86fc',
        marginBottom: '4px',
    },
    cardLocation: {
        fontSize: '13px',
        color: '#ccc',
        marginBottom: '8px',
    },
    cardStatsRow: {
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '6px 12px',
        fontSize: '12px',
        color: '#aaa',
        paddingTop: '8px',
        borderTop: '1px solid #2a2a2a',
    },
    // Dates always take their own line so the card layout doesn't shift
    // depending on whether Max Cap is shown.
    cardDates: {
        flexBasis: '100%',
    },
};

export default FestivalList;
